import { useState, useEffect } from 'react';
import api, { getErrorMessage } from '../utils/api';
import Layout from '../components/Layout';
import { useBranch } from '../contexts/BranchContext';
import CustomerForm from '../components/CustomerForm';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { UserPlus, Phone, QrCode, Image as ImageIcon, PencilSimple, MagnifyingGlass } from '@phosphor-icons/react';

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [qrCustomer, setQrCustomer] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const { activeBranch } = useBranch();

  useEffect(() => {
    if (activeBranch) {
      fetchCustomers();
    }
  }, [activeBranch]);

  useEffect(() => {
    // Filter customers based on search query
    if (!searchQuery.trim()) {
      setFilteredCustomers(customers);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = customers.filter(customer => 
        customer.name.toLowerCase().includes(query) ||
        customer.phone_number.includes(query) ||
        customer.reg_no.toString().includes(query) ||
        (customer.email && customer.email.toLowerCase().includes(query))
      );
      setFilteredCustomers(filtered);
    }
  }, [searchQuery, customers]);

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers', {
        params: { branch_id: activeBranch?.branch_id }
      });
      setCustomers(response.data);
      setFilteredCustomers(response.data);
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCustomer = async (formData) => {
    setSubmitting(true);
    try {
      const res = await api.post('/customers', {
        ...formData,
        branch_id: activeBranch?.branch_id
      });
      toast.success(`Customer added — Registration No. ${res.data?.reg_no}`);
      setShowAddDialog(false);
      fetchCustomers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to add customer'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditCustomer = async (formData) => {
    setSubmitting(true);
    try {
      await api.patch(`/customers/${selectedCustomer.customer_id}`, formData);
      toast.success('Customer updated successfully');
      setShowEditDialog(false);
      setSelectedCustomer(null);
      fetchCustomers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update customer'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (customerId, newStatus) => {
    try {
      await api.patch(`/customers/${customerId}/status`, { status: newStatus });
      toast.success('Status updated');
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const generateQRCode = async (customer) => {
    try {
      const response = await api.get(`/customers/${customer.customer_id}/qr`);
      setQrCode(response.data.qr_code);
      setQrCustomer(customer);
    } catch (error) {
      toast.error('Failed to generate QR code');
    }
  };

  const openEditDialog = (customer) => {
    setSelectedCustomer(customer);
    setShowEditDialog(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Trial':
        return 'bg-blue-100 text-blue-800';
      case 'Follow-up':
        return 'bg-yellow-100 text-yellow-800';
      case 'Purchased':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#2C4C3B] border-r-transparent"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="customers-page">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl tracking-tight font-light text-[#2C4C3B] mb-2">Customers</h1>
            <p className="text-sm sm:text-base text-[#1C1C1A]/60">Manage customer profiles and trial progress</p>
          </div>
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button data-testid="add-customer-button" className="w-full sm:w-auto bg-[#2C4C3B] hover:bg-[#2C4C3B]/90 text-white rounded-md flex items-center justify-center gap-2 py-3 sm:py-2">
                <UserPlus size={20} />
                <span>Add Customer</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white w-[95vw] sm:w-full max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-xl sm:text-2xl font-semibold tracking-tight text-[#2C4C3B]">Add New Customer</DialogTitle>
              </DialogHeader>
              <CustomerForm onSubmit={handleAddCustomer} loading={submitting} />
            </DialogContent>
          </Dialog>
        </div>

        {/* Search Box */}
        <div className="mb-6">
          <div className="relative">
            <MagnifyingGlass 
              size={20} 
              className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#1C1C1A]/40"
            />
            <Input
              data-testid="customer-search-input"
              type="text"
              placeholder="Search by name, phone, registration number, or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 pr-4 py-6 text-base bg-white border-[#E5E5DF] rounded-lg"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[#1C1C1A]/40 hover:text-[#C05C42]"
                data-testid="clear-search-button"
              >
                ✕
              </button>
            )}
          </div>
          {searchQuery && (
            <p className="text-sm text-[#1C1C1A]/60 mt-2">
              Found {filteredCustomers.length} customer{filteredCustomers.length !== 1 ? 's' : ''}
            </p>
          )}
        </div>

        {/* Edit Customer Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="bg-white w-[95vw] max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl sm:text-2xl font-semibold tracking-tight text-[#2C4C3B]">
                Edit Customer - {selectedCustomer?.reg_no}
              </DialogTitle>
            </DialogHeader>
            <CustomerForm 
              onSubmit={handleEditCustomer} 
              loading={submitting} 
              initialData={selectedCustomer}
              isEditMode={true}
            />
          </DialogContent>
        </Dialog>

        <Card className="bg-white border border-[#E5E5DF] rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]" data-testid="customers-table">
              <thead className="bg-[#E2E8E4]">
                <tr>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-[10px] sm:text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A]">Reg No</th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-[10px] sm:text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A]">Name</th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-[10px] sm:text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A]">Phone</th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-[10px] sm:text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A] hidden md:table-cell">Registered</th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-[10px] sm:text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A] hidden lg:table-cell">Sessions</th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-[10px] sm:text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A]">Status</th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-[10px] sm:text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E5DF]">
                {filteredCustomers.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <p className="text-[#1C1C1A]/40">
                        {searchQuery ? 'No customers found matching your search' : 'No customers yet'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredCustomers.map((customer) => (
                  <tr key={customer.customer_id} className="hover:bg-[#F9F9F8] transition-colors">
                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                      <div className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-[#2C4C3B] text-white rounded-lg font-bold text-base sm:text-lg">
                        {customer.reg_no}
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                      <div>
                        <p className="font-medium text-[#2C4C3B] text-sm sm:text-base">{customer.name}</p>
                        {customer.email && <p className="text-xs sm:text-sm text-[#1C1C1A]/60 truncate max-w-[150px]">{customer.email}</p>}
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-[#1C1C1A]/70">
                      <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                        <Phone size={14} className="sm:w-4 sm:h-4" />
                        <span className="hidden sm:inline">{customer.phone_number}</span>
                        <span className="sm:hidden">{customer.phone_number.slice(-4)}</span>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-[#1C1C1A]/70 hidden md:table-cell">
                      <div>
                        <p className="text-sm font-medium text-[#2C4C3B]">
                          {new Date(customer.created_at).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </p>
                        <p className="text-xs text-[#1C1C1A]/60">
                          {new Date(customer.created_at).toLocaleTimeString('en-IN', {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          })}
                        </p>
                        <p className="text-xs sm:text-sm text-[#1C1C1A]/60 truncate max-w-[150px]">{customer.email}</p>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-[#1C1C1A]/70 text-xs sm:text-sm hidden lg:table-cell">
                      {customer.sessions_completed} / {customer.sessions_planned}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                      <Select
                        value={customer.status}
                        onValueChange={(value) => handleStatusChange(customer.customer_id, value)}
                      >
                        <SelectTrigger data-testid={`status-select-${customer.customer_id}`} className="w-24 sm:w-32 text-xs sm:text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Trial">Trial</SelectItem>
                          <SelectItem value="Follow-up">Follow-up</SelectItem>
                          <SelectItem value="Purchased">Purchased</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Button
                          data-testid={`edit-button-${customer.customer_id}`}
                          variant="outline"
                          size="sm"
                          onClick={() => openEditDialog(customer)}
                          className="flex items-center justify-center gap-1 text-xs min-h-[44px] sm:min-h-[36px]"
                        >
                          <PencilSimple size={16} className="sm:w-4 sm:h-4" />
                          <span>Edit</span>
                        </Button>
                        <Button
                          data-testid={`qr-button-${customer.customer_id}`}
                          variant="outline"
                          size="sm"
                          onClick={() => generateQRCode(customer)}
                          className="flex items-center justify-center gap-1 min-h-[44px] sm:min-h-[36px]"
                        >
                          <QrCode size={16} />
                          QR
                        </Button>
                      </div>
                    </td>
                  </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {qrCode && (
          <Dialog open={!!qrCode} onOpenChange={() => setQrCode(null)}>
            <DialogContent className="bg-white">
              <DialogHeader>
                <DialogTitle className="text-2xl font-semibold tracking-tight text-[#2C4C3B]">
                  QR Code for {qrCustomer?.name}
                </DialogTitle>
              </DialogHeader>
              <div className="flex flex-col items-center gap-4 py-4" id="qr-print-area">
                <div className="text-center mb-2">
                  <p className="text-lg font-semibold text-[#2C4C3B]">{qrCustomer?.name}</p>
                  <p className="text-sm text-[#1C1C1A]/60">REG: {qrCustomer?.reg_no}</p>
                  <p className="text-xs text-[#1C1C1A]/50">{qrCustomer?.phone_number}</p>
                </div>
                <img src={qrCode} alt="Customer QR Code" className="w-64 h-64" data-testid="qr-code-image" />
                <p className="text-sm text-[#1C1C1A]/60 text-center">
                  Scan this QR code for quick check-in
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => window.print()}
                  className="flex-1 bg-[#2C4C3B] hover:bg-[#2C4C3B]/90"
                  data-testid="print-qr-button"
                >
                  Print QR Code
                </Button>
                <Button
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = qrCode;
                    link.download = `QR_${qrCustomer?.name}_${qrCustomer?.reg_no}.png`;
                    link.click();
                  }}
                  variant="outline"
                  className="flex-1"
                  data-testid="download-qr-button"
                >
                  Download
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </Layout>
  );
};

export default Customers;